import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { motion } from 'motion/react'
import type { MortgageResult } from '@/lib/mortgage'
import { formatPLN, formatPercent } from '@/lib/constants'

interface ResultsSummaryProps {
  result: MortgageResult
}

function AnimatedValue({ value, className }: { value: string; className?: string }) {
  return (
    <span className={`${className} tabular-nums transition-colors duration-150`}>
      {value}
    </span>
  )
}

const icons = {
  monthlyPayment: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  ),
  loanAmount: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
    </svg>
  ),
  totalCost: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  ),
  totalInterest: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.51l-5.511-3.181" />
    </svg>
  ),
  interestRate: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.848 8.25l1.536.887M7.848 8.25a3 3 0 11-5.196-3 3 3 0 015.196 3zm1.536.887a2.165 2.165 0 011.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 11-5.196 3 3 3 0 015.196-3zm1.536-.887a2.165 2.165 0 001.083-1.838c.005-.352.054-.695.14-1.025m-1.223 2.863l2.077-1.199m0-3.328a4.323 4.323 0 012.068-1.379l5.325-1.628a4.5 4.5 0 012.48-.044l.803.215-7.794 4.5m-2.882-1.664A4.331 4.331 0 0010.607 12m3.736 0l7.794 4.5-.802.215a4.5 4.5 0 01-2.48-.043l-5.326-1.629a4.324 4.324 0 01-2.068-1.379M14.343 12l-2.882 1.664" />
    </svg>
  ),
}


export function ResultsSummary({ result }: ResultsSummaryProps) {
  const { t } = useTranslation()

  const isDecreasing = result.installmentType === 'decreasing'

  const paymentItems = isDecreasing
    ? [
        {
          label: t('results.firstPayment'),
          value: formatPLN(result.monthlyPayment),
          highlight: true,
          iconKey: 'monthlyPayment' as const,
        },
        {
          label: t('results.lastPayment'),
          value: formatPLN(result.lastMonthlyPayment),
          highlight: true,
          iconKey: 'monthlyPayment' as const,
        },
      ]
    : [
        {
          label: t('results.monthlyPayment'),
          value: formatPLN(result.monthlyPayment),
          highlight: true,
          iconKey: 'monthlyPayment' as const,
        },
      ]

  const items = [
    ...paymentItems,
    {
      label: t('results.loanAmount'),
      value: formatPLN(result.loanAmount),
      highlight: false,
      iconKey: 'loanAmount' as const,
    },
    {
      label: t('results.totalCost'),
      value: formatPLN(result.totalCost),
      highlight: false,
      iconKey: 'totalCost' as const,
    },
    {
      label: t('results.totalInterest'),
      value: formatPLN(result.totalInterest),
      highlight: false,
      iconKey: 'totalInterest' as const,
    },
    {
      label: t('results.interestRateLabel'),
      value: formatPercent(result.annualRate),
      highlight: false,
      iconKey: 'interestRate' as const,
    },
  ]

  return (
    <div className="space-y-2">
      <h2 className="text-lg font-bold text-foreground">{t('results.title')}</h2>
      <div className="grid gap-2">
        {items.map((item, index) => (
          <motion.div
            key={`${item.iconKey}-${item.label}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05, duration: 0.3 }}
          >
            <Card
              className={`${
                item.highlight
                  ? 'bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white border-0 glow-pulse'
                  : 'card-hover'
              }`}
            >
              <CardContent className="py-2 px-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={item.highlight ? 'text-white/70' : 'text-muted-foreground'}>
                    {icons[item.iconKey]}
                  </span>
                  <span
                    className={`text-xs ${item.highlight ? 'text-white/80' : 'text-muted-foreground'}`}
                  >
                    {item.label}
                  </span>
                </div>
                <AnimatedValue
                  value={item.value}
                  className={`font-bold ${item.highlight ? 'text-xl text-white' : 'text-base text-foreground'}`}
                />
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  )
}
